import { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import SurveysSidebar from './widgets/SurveysSidebar';
import SurveyContent from './widgets/SurveyContent';
import AddInstantSurveyModal from './widgets/AddInstantSurveyModal';
import {
  fetchInstantSurveys,
  fetchInstantSurveysData, REQUEST_STATUS,
} from '../../redux_store';
import { useParams } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  contentContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(3),
    padding: theme.spacing(0),
    overflow: 'hidden', // Prevent outer scroll
    [theme.breakpoints.down('lg')]: {
      padding: theme.spacing(0),
      gap: theme.spacing(2),
    },
    // Tablet landscape (1024px and down)
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(0),
      gap: theme.spacing(2),
      flexDirection: 'row', // Keep side-by-side on tablets
    },
    // Mobile and small tablets (768px and down)
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0),
      gap: theme.spacing(1.5),
      flexDirection: 'column', // Stack vertically on mobile
    },
  },
  leftColumn: {
    width: 420,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    height: '100%',
    flexShrink: 0,
    minHeight: 0,
    overflow: 'hidden',
    [theme.breakpoints.down('xl')]: {
      width: 380,
    },
    [theme.breakpoints.down('lg')]: {
      width: 340,
    },
    // Tablet landscape (1024px and down)
    [theme.breakpoints.down('md')]: {
      width: 320,
      minWidth: 320,
      flexShrink: 0,
    },
    // Tablet portrait and mobile
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      minWidth: 'unset',
      height: 'auto',
      maxHeight: '40vh', // Limit height on mobile
      flex: '0 0 auto',
    },
  },
  rightColumn: {
    flex: 1,
    background: 'white',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minWidth: 0, // Allow shrinking
    minHeight: 0, // Allow shrinking in height
    // Tablet specific adjustments
    [theme.breakpoints.down('md')]: {
      borderRadius: 12,
      minWidth: 400, // Ensure minimum width for content
    },
    // Mobile adjustments
    [theme.breakpoints.down('sm')]: {
      borderRadius: 8,
      minWidth: 'unset',
      height: 'auto',
      flex: 1,
    },
  },
  sidebarContainer: {
    flex: 1,
    background: '#f8f9fa',
    borderRadius: 16,
    border: '2px solid #e1e4e8',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    minHeight: 0, // Important for flex children with overflow
    // Tablet adjustments
    [theme.breakpoints.down('md')]: {
      borderRadius: 12,
      border: '1px solid #e1e4e8',
    },
    // Mobile adjustments
    [theme.breakpoints.down('sm')]: {
      borderRadius: 8,
      maxHeight: 'inherit',
      minHeight: '200px',
    },
  },
}));

const InstantAssessment = ({values, setFieldValue, setSubmitting, editAccount}) => {
  const classes = useStyles();
  const { en: { titles } } = useSelector((state) => state.i18n);
  const {
    metadata: { currentAccount },
  } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const instantSurveys = values.instantSurveys;
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const { accountUuid, currentAccountUuid } = useParams();

  useEffect(() => {
    if (currentAccount?.uuid) {
      dispatch(fetchInstantSurveysData({ accountUuid: accountUuid ?? currentAccount?.uuid ?? currentAccountUuid }));
    }
  }, [currentAccount?.uuid, dispatch]);

  const handleSurveySelect = (surveyId) => {
    setSelectedSurvey(surveyId);
  };

  const handleAddSurvey = () => {
    setIsAddModalOpen(true);
    console.log('--');
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
  };

  return (
    <>
      <div className={classes.contentContainer}>
        {/* Left Column - Surveys Sidebar */}
        <div className={classes.leftColumn}>
          <div className={classes.sidebarContainer}>
            <SurveysSidebar
              selectedSurvey={selectedSurvey}
              onSurveySelect={handleSurveySelect}
              onAddSurvey={handleAddSurvey}
              instantSurveys={instantSurveys}
            />
          </div>
        </div>

        {/* Right Column - Survey Content */}
        <div className={classes.rightColumn}>
          <SurveyContent
            selectedSurvey={selectedSurvey}
            instantSurveys={instantSurveys}
            setFieldValue={setFieldValue}
            values={values}
            setSubmitting={setSubmitting}
            editAccount={editAccount}
            onSurveySelect={handleSurveySelect}
          />
        </div>
      </div>

      {/* Add Survey Modal */}
      <AddInstantSurveyModal
        open={isAddModalOpen}
        onClose={handleCloseModal}
        setFieldValue={setFieldValue}
        values={values}
        setSubmitting={setSubmitting}
        editAccount={editAccount}
      />
    </>
  );
};

export default InstantAssessment;
